import { motion } from 'motion/react';
import { EASE } from '@/shared/motion/transitions';
import { cn } from './utils';

export interface MiniBarPoint {
  /** Label rendered under the bar (e.g. "27 Apr"). */
  label: string | number;
  /** Bar value. */
  value: number;
  /** Optional emphasis flag — renders the bar in the dark `ink-900` accent. */
  highlight?: boolean;
}

export interface MiniBarChartProps {
  data: MiniBarPoint[];
  /** Height of the bar area only, in px (default 100). Labels/values are outside this. */
  height?: number;
  /** Tone of the bar fill. */
  tone?: 'gold' | 'ink' | 'emerald';
  /** Disable mount animation. */
  animate?: boolean;
  /** Render three faint dotted grid lines behind the bars. */
  gridLines?: boolean;
  /** Show numeric value above each bar. */
  showValues?: boolean;
  className?: string;
}

const TONE_FILL = {
  gold: 'bg-[linear-gradient(90deg,#DCBE85,#A0793D)]',
  ink: 'bg-ink-900',
  emerald: '',
} as const;

/**
 * MiniBarChart — single-row bar chart.
 *
 * The layout is three stacked layers:
 *   1. Value labels row  (optional, fixed height)
 *   2. Bar area          (fixed `height` px, relative — bars grow upward)
 *   3. Date labels row   (fixed height)
 *
 * Grid lines only cover layer 2.
 */
export function MiniBarChart({
  data,
  height = 100,
  tone = 'gold',
  animate = true,
  gridLines = false,
  showValues = false,
  className,
}: MiniBarChartProps) {
  const rawMax = Math.max(...data.map((d) => d.value));
  const allZero = rawMax <= 0;
  const max = allZero ? 1 : rawMax;

  return (
    <div className={cn('w-full', className)}>
      {/* Row 1: value labels */}
      {showValues && (
        <div className="flex gap-1.5 sm:gap-2 md:gap-3 mb-1.5">
          {data.map((d, i) => (
            <span
              key={i}
              className={cn(
                'flex-1 text-center text-[10px] tabular leading-none min-w-0',
                d.value > 0
                  ? d.highlight ? 'text-ink-900 font-semibold' : 'text-ink-500'
                  : d.highlight ? 'text-ink-600 font-semibold' : 'text-ink-300',
              )}
            >
              {d.value}
            </span>
          ))}
        </div>
      )}

      {/* Row 2: bar area */}
      <div className="relative" style={{ height }}>
        {/* Grid lines — absolute behind bars */}
        {gridLines && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex flex-col justify-between"
          >
            {[0, 1, 2].map((i) => (
              <div key={i} className="border-t border-dashed border-ink-100" />
            ))}
          </div>
        )}

        {/* Bars — each column is absolutely positioned so % heights resolve against the bar area */}
        <div className="absolute inset-0 flex gap-1.5 sm:gap-2 md:gap-3">
          {data.map((d, i) => {
            const isEmptyBar = d.value <= 0;
            const pct = allZero || isEmptyBar ? 0 : (d.value / max) * 100;

            if (isEmptyBar) {
              const trackCls = d.highlight ? 'bg-[rgba(112,94,64,0.18)]' : 'bg-[rgba(112,94,64,0.10)]';
              return (
                <div key={i} className="flex-1 relative min-w-0">
                  <div className={cn('absolute bottom-0 left-0 right-0 h-1 rounded-full', trackCls)} />
                </div>
              );
            }

            const fillCls = d.highlight ? 'bg-ink-900' : TONE_FILL[tone];
            const fillStyle =
              !d.highlight && tone === 'emerald'
                ? { background: 'var(--color-emerald)' }
                : undefined;

            return (
              <div key={i} className="flex-1 relative min-w-0">
                {animate ? (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${pct}%` }}
                    transition={{ duration: 0.7, delay: i * 0.05, ease: EASE }}
                    className="absolute bottom-0 left-0 right-0"
                  >
                    <div
                      className={cn('w-full h-full rounded-t-md', fillCls)}
                      style={{ minHeight: 4, ...fillStyle }}
                    />
                  </motion.div>
                ) : (
                  <div
                    className="absolute bottom-0 left-0 right-0"
                    style={{ height: `${pct}%` }}
                  >
                    <div
                      className={cn('w-full h-full rounded-t-md', fillCls)}
                      style={{ minHeight: 4, ...fillStyle }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Row 3: date labels */}
      <div className="flex gap-1.5 sm:gap-2 md:gap-3 mt-2">
        {data.map((d, i) => (
          <span
            key={i}
            className={cn(
              'flex-1 text-center text-[10px] sm:text-[11px] tabular leading-tight min-w-0',
              d.highlight ? 'text-ink-900 font-semibold' : 'text-ink-400',
            )}
          >
            {d.label}
          </span>
        ))}
      </div>
    </div>
  );
}
